import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";

// MUI Icons
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import CoronavirusOutlinedIcon from "@mui/icons-material/CoronavirusOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import apiService from "../../services/actions";

/** Retrieve currently logged-in user code for createdBy payloads */
const getActiveUserCode = () => {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    if (loginObj?.userSession?.userCode) return loginObj.userSession.userCode;
    if (loginObj?.user?.firstName) return loginObj.user.firstName;
  } catch {}
  try {
    const userDetails = JSON.parse(localStorage.getItem("user_details") || "null");
    if (userDetails?.userSession?.userCode) return userDetails.userSession.userCode;
  } catch {}
  return localStorage.getItem("userCode") || localStorage.getItem("access_name") || "admin";
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    let parsed;
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      parsed = dayjs(dateStr);
    } else {
      parsed = dayjs.utc(dateStr).local();
    }
    return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : String(dateStr);
  } catch {
    return String(dateStr);
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "Recent";
  try {
    let parsed;
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      parsed = dayjs(dateStr);
    } else {
      parsed = dayjs.utc(dateStr).local();
    }
    return parsed.isValid() ? parsed.format("DD/MM/YY") : String(dateStr);
  } catch {
    return String(dateStr);
  }
};

const isOrderCancelledOrRouted = (orderStatus) => {
  if (!orderStatus) return false;
  const s = String(orderStatus).trim().toLowerCase();
  return s.includes("cancelled") || s.includes("canceled") || s.includes("routed");
};

// Friendly labels for boolean condition maps
const MEDICAL_HISTORY_LABELS = {
  diabetes: "Diabetes",
  hypertension: "Hypertension",
  thyroidDisorder: "Thyroid Disorder",
  kidneyDisease: "Kidney Disease",
  liverDisease: "Liver Disease",
  asthmaCopd: "Asthma / COPD",
  noKnownMedicalHistory: "No Known Medical History",
};

const DRUG_HISTORY_LABELS = {
  onMedication: "Currently on Medication",
  antibiotics: "Antibiotics",
  steroids: "Steroids",
  bloodThinners: "Blood Thinners",
  noKnownMedicationHistory: "No Known Medication History",
};

const VIRAL_MARKER_LABELS = {
  hiv: "HIV",
  hbsAg: "HBsAg",
  hcv: "HCV",
  knownPositive: "Known Positive",
};

const SPECIAL_CONDITIONS_LABELS = {
  pregnancy: "Pregnancy",
  recentBloodTransfusion: "Recent Blood Transfusion",
  allergyHistory: "Allergy History",
  faintingHistory: "Fainting History",
  noKnownSpecialConditions: "No Known Special Conditions",
};

/** Render a set of condition tags with intuitive visual feedback */
const ConditionChips = ({ dataMap, labelsMap, isViral = false }) => {
  if (!dataMap || typeof dataMap !== "object") {
    return <Typography variant="body2" color="text.secondary">Not recorded</Typography>;
  }

  const entries = Object.entries(labelsMap);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {entries.map(([key, label]) => {
        const val = dataMap[key];
        const isTrue = val === true;
        const isKnownNegative = val === false;

        // Custom styling based on whether it is a reported positive risk or negative
        let bgColor = "#f1f5f9";
        let textColor = "#64748b";
        let borderColor = "#e2e8f0";
        let icon = null;

        if (isTrue) {
          if (key.startsWith("noKnown")) {
            bgColor = "#ecfdf5";
            textColor = "#047857";
            borderColor = "#a7f3d0";
            icon = <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#047857" }} />;
          } else {
            // Replaced red with clean, professional medical blue
            bgColor = "#eff6ff";
            textColor = "#1d4ed8";
            borderColor = "#bfdbfe";
            icon = <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#2563eb" }} />;
          }
        } else if (isKnownNegative) {
          bgColor = "#f8fafc";
          textColor = "#64748b";
          borderColor = "#e2e8f0";
          icon = <CancelOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />;
        } else {
          bgColor = "#f8fafc";
          textColor = "#94a3b8";
          borderColor = "#f1f5f9";
          icon = <HelpOutlineIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />;
        }

        return (
          <Chip
            key={key}
            size="small"
            icon={icon}
            label={`${label}: ${isTrue ? "Yes" : isKnownNegative ? "No" : "N/A"}`}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              border: `1px solid ${borderColor}`,
              fontWeight: isTrue ? 600 : 500,
              fontSize: "0.75rem",
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
        );
      })}
    </Box>
  );
};

ConditionChips.propTypes = {
  dataMap: PropTypes.object,
  labelsMap: PropTypes.object.isRequired,
  isViral: PropTypes.bool,
};

/** Main Component for Tab 2 */
export default function OrderClinicalAndSampleTrack({ order, orderDetails, showSnackbarMessage }) {
  // Extract patientCode and orderId
  const patientCode = useMemo(() => {
    return (
      orderDetails?.patientCode ||
      orderDetails?.patientId ||
      orderDetails?.patientid ||
      orderDetails?.patient_code ||
      order?.patientCode ||
      order?.patientId ||
      order?.patientid ||
      order?.originalData?.patientCode ||
      order?.originalData?.patientId ||
      order?.originalData?.patientid ||
      ""
    );
  }, [orderDetails, order]);

  const orderId = useMemo(() => {
    return orderDetails?.orderId || order?.id || "";
  }, [orderDetails, order]);

  // Section 1: Patient Clinical History state
  const [clinicalList, setClinicalList] = useState([]);
  const [loadingClinical, setLoadingClinical] = useState(false);
  const [clinicalError, setClinicalError] = useState(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);

  // Section 2: Sample Action state
  const [trailList, setTrailList] = useState([]);
  const [loadingTrails, setLoadingTrails] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const isLimitReached = trailList.length >= 3;

  const isCancelled = useMemo(() => {
    return isOrderCancelledOrRouted(order?.orderStatus || orderDetails?.orderStatus);
  }, [order?.orderStatus, orderDetails?.orderStatus]);

  // Fetch Patient Clinical History
  const fetchClinicalHistory = useCallback(async () => {
    if (!patientCode) {
      setClinicalList([]);
      return;
    }
    setLoadingClinical(true);
    setClinicalError(null);
    try {
      const res = await apiService.getPatientClinicalHistory(patientCode);
      if (res?.data?.responseCode === "200" && Array.isArray(res?.data?.successList)) {
        setClinicalList(res.data.successList);
        setSelectedHistoryIndex(0);
      } else if (res?.data?.successList) {
        setClinicalList(res.data.successList);
        setSelectedHistoryIndex(0);
      } else {
        setClinicalList([]);
      }
    } catch (err) {
      console.error("Error fetching patient clinical history:", err);
      setClinicalError("Failed to load clinical history.");
    } finally {
      setLoadingClinical(false);
    }
  }, [patientCode]);

  // Fetch Sample Action Trails
  const fetchActionTrails = useCallback(async () => {
    if (!orderId) {
      setTrailList([]);
      return;
    }
    setLoadingTrails(true);
    try {
      const res = await apiService.getSampleActionTrail(orderId, patientCode, 0, 20);
      if (res?.data?.responseCode === "200" && Array.isArray(res?.data?.successList)) {
        setTrailList(res.data.successList);
      } else if (Array.isArray(res?.data?.successList)) {
        setTrailList(res.data.successList);
      } else {
        setTrailList([]);
      }
    } catch (err) {
      console.error("Error fetching sample action trail:", err);
    } finally {
      setLoadingTrails(false);
    }
  }, [orderId, patientCode]);

  // Initial fetch on mount or when patientCode/orderId changes
  useEffect(() => {
    fetchClinicalHistory();
  }, [fetchClinicalHistory]);

  useEffect(() => {
    fetchActionTrails();
  }, [fetchActionTrails]);

  // Handle Submit Comment for Sample Track POST API
  const handleSubmitComment = async () => {
    if (isCancelled) {
      if (showSnackbarMessage) {
        showSnackbarMessage("Cannot add comments for Cancelled - Routed orders", "warning");
      }
      return;
    }

    const trimmed = commentText.trim();
    if (!trimmed) return;

    if (trailList.length >= 3) {
      if (showSnackbarMessage) {
        showSnackbarMessage("Maximum 3 action comments can be added for an order.", "warning");
      }
      return;
    }

    setSubmittingComment(true);
    const createdBy = getActiveUserCode();

    const payload = {
      patientCode: String(patientCode || ""),
      orderId: String(orderId || ""),
      comments: trimmed,
      createdBy: String(createdBy),
    };

    try {
      const res = await apiService.addSampleActionTrail(payload);
      if (res?.data?.responseCode === "200" || res?.status === 200) {
        if (showSnackbarMessage) {
          showSnackbarMessage(res?.data?.responseMessage || "Comment saved successfully", "success");
        }
        setCommentText("");
        // Automatically refresh trail list
        await fetchActionTrails();
      } else {
        const errorMsg = res?.data?.responseMessage || res?.data?.message || "Failed to save comment";
        if (showSnackbarMessage) {
          showSnackbarMessage(errorMsg, "error");
        }
      }
    } catch (error) {
      console.error("Error submitting sample action comment:", error);
      const errorMsg =
        error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        "Error submitting sample action comment";
      if (showSnackbarMessage) {
        showSnackbarMessage(errorMsg, "error");
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  // Resolve current active clinical history record
  const currentRecord = clinicalList[selectedHistoryIndex] || clinicalList[0] || null;
  const historyData = useMemo(() => {
    if (!currentRecord) return null;
    let ch = currentRecord.clinicalHistory;
    if (typeof ch === "string") {
      try {
        ch = JSON.parse(ch);
      } catch {}
    }
    return ch || {};
  }, [currentRecord]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}>
      {/* ========================================================================= */}
      {/* SECTION 1: PATIENT CLINICAL HISTORY                                       */}
      {/* ========================================================================= */}
      <Card sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                backgroundColor: "#e0e7ff",
                color: "#4338ca",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MedicalServicesOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="700" color="#1e293b">
                Patient Clinical History
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Patient Code: <strong>{patientCode || "Not Available"}</strong>
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh Clinical History">
            <span>
              <IconButton size="small" onClick={fetchClinicalHistory} disabled={loadingClinical}>
                {loadingClinical ? <CircularProgress size={18} /> : <RefreshOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          {loadingClinical ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1.5 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Fetching patient clinical history...
              </Typography>
            </Box>
          ) : clinicalError ? (
            <Alert severity="error" sx={{ mb: 1 }}>{clinicalError}</Alert>
          ) : !clinicalList || clinicalList.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <MedicalServicesOutlinedIcon sx={{ fontSize: 44, color: "#cbd5e1", mb: 1 }} />
              <Typography variant="body1" fontWeight="600" color="#64748b">
                No Clinical History Found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No clinical history records are currently available for patient code: {patientCode || "—"}.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* If multiple records exist, show selector tabs */}
              {clinicalList.length > 1 && (
                <Box sx={{ borderBottom: "1px solid #e2e8f0" }}>
                  <Tabs
                    value={selectedHistoryIndex}
                    onChange={(e, val) => setSelectedHistoryIndex(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 36 }}
                  >
                    {clinicalList.map((rec, idx) => (
                      <Tab
                        key={rec.id || idx}
                        label={`Record #${idx + 1} (${formatDate(rec.createdDate)})`}
                        sx={{ minHeight: 36, py: 0.5, fontSize: "0.8rem", textTransform: "none" }}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}

              {/* Fasting Status Banner */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.75,
                  backgroundColor: historyData?.fastingStatus?.isFasting ? "#ecfdf5" : "#f8fafc",
                  border: `1px solid ${historyData?.fastingStatus?.isFasting ? "#a7f3d0" : "#e2e8f0"}`,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RestaurantOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: historyData?.fastingStatus?.isFasting ? "#047857" : "#64748b",
                    }}
                  />
                  <Typography variant="body2" fontWeight="600" color="#1e293b">
                    Fasting Status:
                  </Typography>
                  <Chip
                    size="small"
                    label={historyData?.fastingStatus?.isFasting ? "Fasting: Yes" : "Fasting: No"}
                    sx={{
                      backgroundColor: historyData?.fastingStatus?.isFasting ? "#10b981" : "#94a3b8",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>
                {historyData?.fastingStatus?.isFasting && historyData?.fastingStatus?.hours !== undefined && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "#047857" }} />
                    <Typography variant="body2" fontWeight="600" color="#047857">
                      {historyData.fastingStatus.hours} hours
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Grid container spacing={2}>
                {/* Medical History */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, backgroundColor: "#fff" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <MedicalServicesOutlinedIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                      <Typography variant="subtitle2" fontWeight="700" color="#334155">
                        Medical History
                      </Typography>
                    </Box>
                    <ConditionChips
                      dataMap={historyData?.medicalHistory}
                      labelsMap={MEDICAL_HISTORY_LABELS}
                    />
                  </Box>
                </Grid>

                {/* Drug History */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, backgroundColor: "#fff" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <MedicationOutlinedIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />
                      <Typography variant="subtitle2" fontWeight="700" color="#334155">
                        Drug & Medication History
                      </Typography>
                    </Box>
                    <ConditionChips
                      dataMap={historyData?.drugHistory}
                      labelsMap={DRUG_HISTORY_LABELS}
                    />
                  </Box>
                </Grid>

                {/* Viral Marker History */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, backgroundColor: "#fff" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <CoronavirusOutlinedIcon sx={{ fontSize: 18, color: "#0284c7" }} />
                      <Typography variant="subtitle2" fontWeight="700" color="#334155">
                        Viral Marker History
                      </Typography>
                    </Box>
                    <ConditionChips
                      dataMap={historyData?.viralMarkerHistory}
                      labelsMap={VIRAL_MARKER_LABELS}
                      isViral
                    />
                  </Box>
                </Grid>

                {/* Special Conditions */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, backgroundColor: "#fff" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <ScienceOutlinedIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                      <Typography variant="subtitle2" fontWeight="700" color="#334155">
                        Special Conditions
                      </Typography>
                    </Box>
                    <ConditionChips
                      dataMap={historyData?.specialConditions}
                      labelsMap={SPECIAL_CONDITIONS_LABELS}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Clinical History Notes */}
              {/* {historyData?.clinicalHistory && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <NotesOutlinedIcon sx={{ fontSize: 18, color: "#0284c7" }} />
                    <Typography variant="subtitle2" fontWeight="700" color="#0369a1">
                      Clinical Notes / Observations
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#0c4a6e" sx={{ whiteSpace: "pre-line" }}>
                    {historyData.clinicalHistory}
                  </Typography>
                </Paper>
              )} */}

              {/* Remarks */}
              {historyData?.remarks && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#fefce8",
                    border: "1px solid #fef08a",
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <CommentOutlinedIcon sx={{ fontSize: 18, color: "#ca8a04" }} />
                    <Typography variant="subtitle2" fontWeight="700" color="#a16207">
                      Remarks
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#713f12" sx={{ whiteSpace: "pre-line" }}>
                    {historyData.remarks}
                  </Typography>
                </Paper>
              )}

              {/* Metadata Footer */}
              <Box
                sx={{
                  pt: 1.5,
                  borderTop: "1px dashed #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Recorded By: <strong>{currentRecord.createdBy || "—"}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Recorded Date: <strong>{formatDateTime(currentRecord.createdDate)}</strong>
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ========================================================================= */}
      {/* SECTION 2: SAMPLE TRACK (ACTION TRAIL)                                     */}
      {/* ========================================================================= */}
      <Card sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                backgroundColor: "#fdf2f8",
                color: "#db2777",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ScienceOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="700" color="#1e293b">
                Sample Action
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Order ID: <strong>{orderId || "—"}</strong>
                </Typography>
                {patientCode && (
                  <Typography variant="caption" color="text.secondary">
                    • Patient Code: <strong>{patientCode}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Tooltip title="Refresh Sample Actions">
            <span>
              <IconButton size="small" onClick={fetchActionTrails} disabled={loadingTrails}>
                {loadingTrails ? <CircularProgress size={18} /> : <RefreshOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          {/* Post New Comment / Action */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="700" color="#334155">
                Add Sample Action Comment
              </Typography>
              <Chip
                size="small"
                label={`${trailList.length}/3 Comments Added`}
                color={isLimitReached ? "error" : trailList.length === 2 ? "warning" : "default"}
                variant={isLimitReached ? "filled" : "outlined"}
                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
              />
            </Box>

            {isCancelled ? (
              <Alert
                severity="warning"
                sx={{
                  py: 0.5,
                  px: 1.5,
                  fontSize: "0.8125rem",
                  mb: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: "#fffbeb",
                  color: "#92400e",
                  border: "1px solid #fde68a",
                }}
              >
                Sample action comments cannot be added for Cancelled - Routed orders.
              </Alert>
            ) : isLimitReached ? (
              <Alert
                severity="info"
                sx={{
                  py: 0.5,
                  px: 1.5,
                  fontSize: "0.8125rem",
                  mb: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: "#eff6ff",
                  color: "#1e40af",
                  border: "1px solid #bfdbfe",
                  "& .MuiAlert-icon": { color: "#3b82f6", fontSize: 20 },
                }}
              >
                Only 3 action comments can be added per order. The limit has been reached.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Note: Only up to 3 action comments can be added ({3 - trailList.length} remaining).
              </Typography>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              placeholder={
                isCancelled
                  ? "Adding comments is not allowed for Cancelled - Routed orders."
                  : isLimitReached
                  ? "Maximum limit of 3 action comments reached for this order."
                  : "Please enter action comment (e.g., Please recollect the sample)..."
              }
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={submittingComment || isLimitReached || isCancelled}
              sx={{
                backgroundColor: isLimitReached || isCancelled ? "#f1f5f9" : "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Posting as: <strong>{getActiveUserCode()}</strong>
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {commentText.trim() && !isLimitReached && !isCancelled && (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={() => setCommentText("")}
                    disabled={submittingComment}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || submittingComment || isLimitReached || isCancelled}
                  startIcon={submittingComment ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 2,
                    backgroundColor: isLimitReached || isCancelled ? "#94a3b8" : "#7c3aed",
                    "&:hover": { backgroundColor: isLimitReached || isCancelled ? "#94a3b8" : "#6d28d9" },
                    "&.Mui-disabled": {
                      backgroundColor: "#e2e8f0",
                      color: "#94a3b8",
                    },
                  }}
                >
                  {submittingComment ? "Saving..." : "Add Comment"}
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Action Trail List */}
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="700" color="#334155">
                Action Trail History
              </Typography>
              <Chip
                label={`${trailList.length} of 3 Max`}
                size="small"
                color={isLimitReached ? "default" : "secondary"}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              />
            </Box>

            {loadingTrails ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3, gap: 1 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Loading sample action trails...
                </Typography>
              </Box>
            ) : trailList.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  border: "1px dashed #cbd5e1",
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",
                }}
              >
                <CommentOutlinedIcon sx={{ fontSize: 36, color: "#94a3b8", mb: 1 }} />
                <Typography variant="body2" fontWeight="600" color="#64748b">
                  No Action Trails Recorded
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No sample action comments have been posted for this order yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {trailList.map((item, index) => (
                  <Paper
                    key={item.id || index}
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderLeft: "4px solid #7c3aed",
                      borderRadius: 1.5,
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1e293b",
                        fontWeight: 500,
                        whiteSpace: "pre-line",
                        lineHeight: 1.5,
                        mb: 1.5,
                      }}
                    >
                      {item.comments || "—"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        pt: 1,
                        borderTop: "1px dashed #e2e8f0",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonOutlineIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {item.createdBy || "Unknown User"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(item.createdDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

OrderClinicalAndSampleTrack.propTypes = {
  order: PropTypes.object,
  orderDetails: PropTypes.object,
  showSnackbarMessage: PropTypes.func,
};
